'use client';

import { useMemo, useState, useEffect } from 'react';
import './VisualizationPanel.scss';
import dynamic from 'next/dynamic';
import PlotlyFallback from './PlotlyFallback';

// Moved heavy WebGPU visualization (and its WASM dependency chain) into a lazily-loaded chunk.
const GPUVisualizer = dynamic(() => import('./GPUVisualizer'), {
  ssr: false,
  loading: () => <div style={{ color: '#888', fontSize: 12, padding: 8 }}>Loading WebGPU renderer...</div>,
});

// Types
interface VisualizationPanelProps {
  plotData: any;
  plotLayout: any;
  svgThumbnail?: string | null;
}

// Schema for Data (composite array-of-structs: each record is one polyline)
const LINE_SCHEMA = {
  positions: 'array<vec3<f32>>',
  colors: 'vec4<f32>',
  sizes: 'f32',
} as const;

/**
  Convert Plotly-style scatter3d line traces into Data records understood by <Data>
  - Each contiguous polyline (split on NaN/null) becomes one record { positions, color, size }
  - positions is flat [x,y,z,...]
*/
function plotlyToLineRecords(plotData: any) {
  const records: { positions: number[]; colors: [number, number, number, number]; sizes: number }[] = [];

  let minX = +Infinity, minY = +Infinity, minZ = +Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

  const updateBounds = (x: number, y: number, z: number) => {
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
    if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
  };

  const parseColor = (c: any): [number, number, number, number] => {
    if (!c) return [0.2, 0.6, 1.0, 1.0];
    if (Array.isArray(c)) {
      const [r, g, b, a = 1] = c;
      if (r > 1 || g > 1 || b > 1 || a > 1) return [r / 255, g / 255, b / 255, a > 1 ? a / 255 : a];
      return [r, g, b, a];
    }
    if (typeof c === 'string') {
      const hex = c.startsWith('#') ? c : null;
      if (hex) {
        const v = hex.length === 7 ? parseInt(hex.slice(1), 16) : null;
        if (v != null) return [((v >> 16) & 0xff) / 255, ((v >> 8) & 0xff) / 255, (v & 0xff) / 255, 1];
      }
      const m = c.match(/rgba?\(([^)]+)\)/i);
      if (m) {
        const parts = m[1].split(',').map(s => parseFloat(s.trim()));
        const [rr, gg, bb, aa = 1] = parts; return [rr / 255, gg / 255, bb / 255, aa];
      }
    }
    return [0.2, 0.6, 1.0, 1.0];
  };

  const isFiniteTriple = (x: any, y: any, z: any) => Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z);
  const traces: any[] = Array.isArray(plotData) ? plotData : [];

  for (const t of traces) {
    if (!t || t.type !== 'scatter3d') continue;
    const mode = t.mode || 'lines';
    if (!(typeof mode === 'string' && mode.includes('lines'))) continue;

    const xs = t.x || []; const ys = t.y || []; const zs = t.z || [];
    const width = (t.line && (t.line.width as number)) || 1.5;
    const rgba = parseColor(t.line && t.line.color);

    let current: number[] = [];

    const flush = () => {
      if (current.length >= 6) { // at least 2 points
        records.push({ positions: current, colors: rgba, sizes: width });
      }
      current = [];
    };

    for (let i = 0; i < xs.length; i++) {
      const x = Number(xs[i]); const y = Number(ys[i]); const z = Number(zs[i]);
      if (!isFiniteTriple(x, y, z)) { flush(); continue; }
      current.push(x, y, z);
      updateBounds(x, y, z);
    }
    flush();
  }

  const hasData = records.length > 0;
  const bounds = hasData ? {
    min: [minX, minY, minZ] as [number, number, number],
    max: [maxX, maxY, maxZ] as [number, number, number],
  } : null;

  return { records, bounds };
}

export default function VisualizationPanel({
  plotData,
  plotLayout,
  svgThumbnail,
}: VisualizationPanelProps) {
  const [mounted, setMounted] = useState(false);
  const [webGPUSupported, setWebGPUSupported] = useState<boolean | null>(null);
  const [forceFallback, setForceFallback] = useState(false);
  const [visibleSteps, setVisibleSteps] = useState<number | null>(null); // null means show all
  const [isPlaying, setIsPlaying] = useState(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [isInputFocused, setIsInputFocused] = useState(false);

  // Client-side WebGPU detection
  useEffect(() => {
    const detectWebGPU = async () => {
      if (typeof window !== 'undefined' && 'gpu' in navigator) {
        try {
          const gpu = (navigator as any).gpu;
          const adapter = await gpu?.requestAdapter();
          setWebGPUSupported(!!adapter);
        } catch {
          setWebGPUSupported(false);
        }
      } else {
        setWebGPUSupported(false);
      }
    };
    detectWebGPU();
  }, []);

  // const [showLegacyInfo, setShowLegacyInfo] = useState(true); // Legacy info dialog (temporarily disabled)
  const converted = useMemo(() => {
    if (!plotData) return null;
    return plotlyToLineRecords(plotData);
  }, [plotData]);

  // Get total step count and build cumulative step index for faster filtering
  const recordStepInfo = useMemo(() => {
    if (!converted?.records) return { totalSteps: 0, cumulativeSteps: [] };

    const cumulativeSteps: number[] = [];
    let total = 0;

    for (const record of converted.records) {
      const pointCount = Math.floor(record.positions.length / 3);
      total += pointCount;
      cumulativeSteps.push(total);
    }

    return { totalSteps: total, cumulativeSteps };
  }, [converted]);

  const totalSteps = recordStepInfo.totalSteps;

  // Initialize visibleSteps to totalSteps when data loads
  useEffect(() => {
    if (totalSteps > 0 && visibleSteps === null) {
      setVisibleSteps(totalSteps);
      setInputValue(totalSteps.toString());
    }
  }, [totalSteps, visibleSteps]);

  // Update input value when visibleSteps changes externally (slider drag)
  useEffect(() => {
    if (visibleSteps !== null && !isInputFocused) {
      setInputValue(visibleSteps.toString());
    }
  }, [visibleSteps, isInputFocused]);

  // Animation effect for play/pause with performance optimization
  useEffect(() => {
    if (!isPlaying || visibleSteps === null || totalSteps === 0) return;

    let animationFrameId: number;
    let lastUpdateTime = performance.now();
    const updateInterval = 50; // Update every 50ms for smoother animation

    const animate = (currentTime: number) => {
      if (currentTime - lastUpdateTime >= updateInterval) {
        setVisibleSteps(prev => {
          if (prev === null || prev >= totalSteps) {
            setIsPlaying(false);
            return totalSteps;
          }
          // Balanced increment for smooth animation, adjust speed based on total steps
          const increment = Math.max(5, Math.floor(totalSteps / 400));
          return Math.min(prev + increment, totalSteps);
        });
        lastUpdateTime = currentTime;
      }

      if (isPlaying) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isPlaying, totalSteps]);

  // Throttle filtered data updates during animation to reduce render frequency
  const [throttledVisibleSteps, setThrottledVisibleSteps] = useState<number | null>(null);

  useEffect(() => {
    // Immediately update if not playing (user interaction)
    if (!isPlaying) {
      setThrottledVisibleSteps(visibleSteps);
      return;
    }

    // Lighter throttle for smoother animation during playback
    const timeoutId = setTimeout(() => {
      setThrottledVisibleSteps(visibleSteps);
    }, 30); // 30ms throttle for smoother updates

    return () => clearTimeout(timeoutId);
  }, [visibleSteps, isPlaying]);

  // Use throttled steps for rendering
  const displaySteps = isPlaying ? throttledVisibleSteps : visibleSteps;

  // Filter records based on step count with optimized lookup
  const filteredConverted = useMemo(() => {
    if (!converted || displaySteps === null || displaySteps >= totalSteps) return converted;

    const { records, bounds } = converted;
    const { cumulativeSteps } = recordStepInfo;

    if (!records.length || displaySteps <= 0) return converted;

    // Binary search to find which record index contains the visible step threshold
    let left = 0;
    let right = cumulativeSteps.length - 1;
    let lastFullRecordIndex = -1;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const prevSteps = mid > 0 ? cumulativeSteps[mid - 1] : 0;

      if (prevSteps < displaySteps) {
        lastFullRecordIndex = mid;
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    if (lastFullRecordIndex === -1) return { records: [], bounds };

    // Include all complete records up to lastFullRecordIndex
    const filteredRecords = records.slice(0, lastFullRecordIndex);

    // Check if we need a partial record
    const prevSteps = lastFullRecordIndex > 0 ? cumulativeSteps[lastFullRecordIndex - 1] : 0;
    const stepsInCurrentRecord = displaySteps - prevSteps;

    if (stepsInCurrentRecord > 0 && lastFullRecordIndex < records.length) {
      const record = records[lastFullRecordIndex];
      const pointsNeeded = stepsInCurrentRecord * 3;

      if (pointsNeeded < record.positions.length) {
        // Add partial record
        filteredRecords.push({
          positions: record.positions.slice(0, pointsNeeded),
          colors: record.colors,
          sizes: record.sizes
        });
      } else {
        // Add complete record
        filteredRecords.push(record);
      }
    }

    return { records: filteredRecords, bounds };
  }, [converted, displaySteps, totalSteps, recordStepInfo]);

  // Set mounted after first render (to avoid hydration mismatch)
  if (!mounted) {
    setMounted(true);
  }

  // Determine which renderer to use: prefer WebGPU if supported, unless manually overridden
  const shouldUseFallback = forceFallback || webGPUSupported === false;

  // Filter plotData for Plotly fallback based on step count
  const filteredPlotData = useMemo(() => {
    if (!plotData || displaySteps === null || displaySteps >= totalSteps) return plotData;

    let remainingSteps = displaySteps;

    // Filter Plotly traces
    return plotData.map((trace: any) => {
      if (!trace || trace.type !== 'scatter3d') return trace;
      if (remainingSteps <= 0) return { ...trace, x: [], y: [], z: [] };

      const xs = trace.x || [];
      const ys = trace.y || [];
      const zs = trace.z || [];

      const newXs: any[] = [];
      const newYs: any[] = [];
      const newZs: any[] = [];

      for (let i = 0; i < xs.length; i++) {
        if (remainingSteps <= 0) break;

        const x = xs[i];
        const y = ys[i];
        const z = zs[i];

        newXs.push(x);
        newYs.push(y);
        newZs.push(z);

        // Count finite points as steps
        if (Number.isFinite(Number(x)) && Number.isFinite(Number(y)) && Number.isFinite(Number(z))) {
          remainingSteps--;
        }
      }

      return { ...trace, x: newXs, y: newYs, z: newZs };
    });
  }, [plotData, displaySteps, totalSteps]);

  // Handler for slider drag - pauses playback
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    setVisibleSteps(value);
    setIsPlaying(false); // Pause when manually dragging
  };

  // Handler for input field changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsPlaying(false); // Pause when editing
  };

  // Handler for input field blur - validate and update
  const handleInputBlur = () => {
    setIsInputFocused(false);
    const value = parseInt(inputValue, 10);
    if (!isNaN(value)) {
      const clampedValue = Math.max(0, Math.min(value, totalSteps));
      setVisibleSteps(clampedValue);
      setInputValue(clampedValue.toString());
    } else {
      // Reset to current value if invalid
      setInputValue((visibleSteps ?? totalSteps).toString());
    }
  };

  // Handler for input field key press
  const handleInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  // Toggle play/pause
  const togglePlayPause = () => {
    if (visibleSteps === totalSteps) {
      // If at end, restart from beginning
      setVisibleSteps(0);
      setIsPlaying(true);
    } else {
      setIsPlaying(!isPlaying);
    }
  };

  // Expose for fallback (simple global stash)
  if (typeof window !== 'undefined') {
    (window as any).__plotData = filteredPlotData;
    (window as any).__plotLayout = plotLayout;
  }

  return (
    <div className="preview-panel">
      <div className="panel-header">
        <div className="panel-title">
          <h3>
            3D Visualization
          </h3>
          {webGPUSupported && (
            <span style={{ marginLeft: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
              Using {shouldUseFallback ? 'Plotly' : 'WebGPU'}
              <button
                style={{ marginLeft: 8, padding: "2px 6px" }}
                onClick={() => setForceFallback(f => !f)}
                title={shouldUseFallback ? 'Switch to WebGPU Renderer' : 'Switch to Legacy (Plotly) Renderer'}
              >
                {shouldUseFallback ? 'Switch to WebGPU' : 'Switch to Plotly'}
              </button>
            </span>
          )}
          {webGPUSupported === false && (
            <span style={{ marginLeft: 8, fontSize: 11, color: '#888' }}>
              WebGPU not available, using Plotly renderer
            </span>
          )}
        </div>
      </div>

      <div className="visualization-panel" style={{ background: '#111' }}>
        {/* Layer preview slider */}
        {totalSteps > 0 && (
          <div className="layer-preview-slider-container">
            <div className="layer-preview-controls">
              <input
                type="text"
                className="layer-preview-input"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={() => setIsInputFocused(true)}
                onBlur={handleInputBlur}
                onKeyPress={handleInputKeyPress}
                title="Current step"
              />
              <br />
              <span className="layer-preview-separator">/</span>
              <span className="layer-preview-total">{totalSteps}</span>
            </div>
            <input
              type="range"
              {...({ orient: 'vertical' } as any)}
              min="0"
              max={totalSteps}
              value={visibleSteps ?? totalSteps}
              onChange={handleSliderChange}
              className="layer-preview-slider"
              title={`Show ${visibleSteps ?? totalSteps} of ${totalSteps} steps`}
            />
            <button
              className="layer-preview-play-button"
              onClick={togglePlayPause}
              title={isPlaying ? 'Pause animation' : 'Play animation'}
            >
              {isPlaying ? '⏸' : '▶'}
            </button>
          </div>
        )}

        {shouldUseFallback ? (
          <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <PlotlyFallback plotData={filteredPlotData} plotLayout={plotLayout} svgThumbnail={svgThumbnail} />
          </div>
        ) : (
          mounted && webGPUSupported ? (
            <GPUVisualizer records={filteredConverted?.records || []} bounds={filteredConverted?.bounds || null} />
          ) : <div style={{ color: '#888', fontSize: 12, padding: 8 }}>
            {webGPUSupported === null ? 'Detecting WebGPU support...' : 'Initializing WebGPU...'}
          </div>
        )}
      </div>
    </div>
  );
}